import { type ReactNode, useState } from 'react';

import { Text, theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { YoutubeVideoButton } from 'Components/YoutubeVideoButton';

import { howItWorksContent } from 'assets/content/homeV2';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { HowItWorksCard } from './HowItWorksCard';
import { HowItWorksMobileSlider } from './HowItWorksSlider';

const Section = styled.div`
  width: 100%;
  height: 600px;

  display: flex;
  justify-content: center;

  position: relative;
  padding: 0 ${theme.spacing['4x']};

  ${legacyTheme.breakpoints.up('md')} {
    height: 443px;
    padding: 0 ${theme.spacing['8x']};
  }
`;

const BackgroundRadius = styled.div`
  width: 100%;
  height: 600px;

  background-color: ${theme.colors.neutral[300]};
  border-radius: ${theme.radius.xs};

  ${legacyTheme.breakpoints.up('md')} {
    position: absolute;
    z-index: -1;
    top: 13px;
    height: 430px;
  }
`;

const MobileSection = styled.div`
  width: 100%;

  position: absolute;
  top: 20%;
`;

const TitleContainer = styled.div`
  width: 200px;

  ${legacyTheme.breakpoints.up('sm')} {
    width: auto;
  }
  ${legacyTheme.breakpoints.up('md')} {
    width: 200px;
  }
`;

const DesktopBackgroundCardContainer = styled.div`
  width: 100%;
  height: 443px;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  position: relative;
  overflow: hidden;
`;

const DesktopContentWrapper = styled.div`
  ${legacyTheme.breakpoints.up('md')} {
    align-items: flex-start;
    justify-content: flex-start;

    height: 430px;
    margin-bottom: 0;
    padding: 0 ${theme.spacing['4x']};
  }
`;

const DesktopAllContentWrapper = styled.div`
  width: 100%;
  height: 100%;

  display: flex;

  ${legacyTheme.breakpoints.up('md')} {
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
  }
  ${legacyTheme.breakpoints.up('lg')} {
    width: 100%;
    max-width: 1480px;
    margin: 0 auto;
  }
`;

const DesktopCardsWrapper = styled.div`
  width: 80%;
  height: 100%;

  display: flex;
  gap: ${theme.spacing['6x']};
`;

const DesktopCardWrapper = styled.div<{ selected: boolean }>`
  box-shadow: ${({ selected }) => (selected ? `0 -13px 0 ${theme.colors.neutral[400]};` : 'none')};
  transform: ${({ selected }) => (selected ? 'translateY(0)' : 'translateY(32%)')};
  flex: 1;
  background: ${theme.colors.neutral[200]};
  height: 100%;
  transition: transform 0.5s ease-in-out;
  cursor: pointer;
`;

const TitleBlockWrapper = styled.div`
  width: 100%;
  height: 100%;

  padding: ${theme.spacing['8x']} 0;

  ${legacyTheme.breakpoints.up('sm')} {
    padding: ${theme.spacing['4x']};
  }

  ${legacyTheme.breakpoints.up('md')} {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    width: 20%;
  }
`;

const TitleAndButtonWrapper = styled.div`
  height: 100%;

  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: space-between;

  margin: 0 auto;

  ${legacyTheme.breakpoints.up('md')} {
    align-items: flex-start;
    justify-content: center;
    width: 100%;
  }

  ${legacyTheme.breakpoints.up('sm')} {
    gap: ${theme.spacing['4x']};
  }
`;

const TabletCardsWrapper = styled.div`
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  gap: ${theme.spacing['4x']};
`;

type HowItWorksVideoModalButtonProps = {
  onOpenVideo: () => void;
};

type HowItWorksTitleBlockProps = {
  onOpenVideo: () => void;
  children?: ReactNode;
  title: string;
};

const HowItWorksTitleBlock = ({ onOpenVideo, title, children }: HowItWorksTitleBlockProps) => (
  <TitleBlockWrapper>
    <TitleAndButtonWrapper>
      <TitleContainer>
        <Text
          align={{
            mobile: 'center',
            desktop: 'left',
          }}
          as="h2"
          variant="heading5"
        >
          {title}
        </Text>
      </TitleContainer>
      {children}
      <YoutubeVideoButton
        buttonText="See how it works"
        dataClick="see-how-it-works"
        dataFrom="homepage-how-it-works-module"
        dataTestId="see-how-it-works-button"
        onOpenVideo={onOpenVideo}
      />
    </TitleAndButtonWrapper>
  </TitleBlockWrapper>
);

export const HowItWorks = ({ onOpenVideo }: HowItWorksVideoModalButtonProps) => {
  const { title, cards } = howItWorksContent;
  const { isMobile, isDesktop, isTablet } = useResponsiveVariant();
  const [isUp, setIsUp] = useState<number>(1);
  return (
    <Section data-testid="home-revamp-how-it-works">
      {isDesktop && (
        <DesktopBackgroundCardContainer>
          <>
            <DesktopContentWrapper>
              <DesktopAllContentWrapper>
                <HowItWorksTitleBlock onOpenVideo={onOpenVideo} title={title} />
                <DesktopCardsWrapper>
                  {cards.map((card, i) => (
                    <DesktopCardWrapper
                      key={card.title}
                      onMouseEnter={() => setIsUp(i)}
                      onMouseLeave={() => setIsUp(1)}
                      selected={isUp === i}
                    >
                      <HowItWorksCard card={card} index={i + 1} />
                    </DesktopCardWrapper>
                  ))}
                </DesktopCardsWrapper>
              </DesktopAllContentWrapper>
            </DesktopContentWrapper>
            <BackgroundRadius />
          </>
        </DesktopBackgroundCardContainer>
      )}

      {!isDesktop && (
        <>
          <BackgroundRadius>
            <HowItWorksTitleBlock onOpenVideo={onOpenVideo} title={title}>
              {isTablet && (
                <TabletCardsWrapper>
                  {cards.map((card, i) => (
                    <HowItWorksCard key={card.title} card={card} index={i + 1} />
                  ))}
                </TabletCardsWrapper>
              )}
            </HowItWorksTitleBlock>
          </BackgroundRadius>
          {isMobile && (
            <MobileSection>
              <HowItWorksMobileSlider cards={cards} />
            </MobileSection>
          )}
        </>
      )}
    </Section>
  );
};
